/**
 * Smart Shikka AI Core — PR94
 * Controlled Provider Healthcheck — Owner Approved / No User Prompt
 * Registry — Deep Frozen — Synthetic Payload Only
 */
function deepFreeze(obj) {
    if (obj && typeof obj === "object") {
        Object.freeze(obj);
        for (const key of Object.keys(obj)) {
            const val = obj[key];
            if (val && typeof val === "object" && !Object.isFrozen(val)) {
                deepFreeze(val);
            }
        }
    }
    return obj;
}
const BLOCKED_ACTIONS = [
    "agent_execution", "chat_runtime_start", "child_process",
    "file_read", "file_write", "message_send",
    "old_assistant_alias", "secret_literal", "shell_command",
    "tool_execution", "unapproved_db_write", "unapproved_file_write",
    "unapproved_network_call", "unknown_provider_call", "unknown_tool_execution",
    "unredacted_secret", "unsafe_env_dump", "user_prompt_send",
    "runtime_unlock", "gate_unlock", "db_write",
    "supabase_call", "browser_action", "desktop_action",
    "render_document", "inject_icon_runtime", "workflow_execution",
    "secret_read",
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    userPromptAllowedInPr94: false,
    chatSendAllowedInPr94: false,
    providerCallWithUserDataAllowedInPr94: false,
    secretLiteralAllowedInPr94: false,
    secretReadAllowedInPr94: false,
    runtimeUnlockAllowedInPr94: false,
    gateUnlockAllowedInPr94: false,
    agentExecutionAllowedInPr94: false,
    toolExecutionAllowedInPr94: false,
    workflowExecutionAllowedInPr94: false,
    messageSendAllowedInPr94: false,
    chatRuntimeAllowedInPr94: false,
    documentRenderAllowedInPr94: false,
    iconInjectionAllowedInPr94: false,
    browserAutomationAllowedInPr94: false,
    desktopAutomationAllowedInPr94: false,
    fileMutationAllowedInPr94: false,
    fileReadAllowedInPr94: false,
    networkWithUserDataAllowedInPr94: false,
    dbWriteAllowedInPr94: false,
    supabaseAllowedInPr94: false,
    shellCommandAllowedInPr94: false,
    childProcessAllowedInPr94: false,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
};
const registry = [
    {
        id: "provider_healthcheck_summary",
        displayName: "Provider Healthcheck Summary",
        description: "Controlled server-side provider healthcheck. Owner-approved, staging-only, synthetic payload, fully redacted evidence. No user prompt.",
        sourceModules: [
            "smart-shikka-assistant-provider-settings-secret-reference",
            "smart-shikka-assistant-orchestrator",
        ],
        targetPreviewModules: ["smart-shikka-assistant-provider-healthcheck"],
        relatedRouteIds: ["capability_discovery_request", "safety_guardrail_explain_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["provider_call_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_provider_call_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_provider_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Healthcheck is server-side only", "No user prompt or chat message", "Synthetic payload only"],
        futurePrScope: ["Live healthcheck execution with owner approval", "Per-provider healthcheck gate"],
        notes: ["Owner-approved controlled healthcheck", "Staging-only environment"],
    },
    {
        id: "owner_approved_healthcheck_gate",
        displayName: "Owner-Approved Healthcheck Gate",
        description: "Gate requiring owner approval before any healthcheck can execute. No user prompt bypass.",
        sourceModules: [
            "smart-shikka-assistant-provider-healthcheck",
            "smart-shikka-assistant-readonly-execution-gate-matrix-preview",
        ],
        targetPreviewModules: ["smart-shikka-assistant-provider-healthcheck"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_owner_approval_required_state"],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Owner approval required", "No bypass of approval gate", "No user-triggered healthcheck"],
        futurePrScope: ["Owner approval workflow integration"],
        notes: ["Dual approval required", "Gate locked until explicit owner action"],
    },
    {
        id: "synthetic_healthcheck_payload_contract",
        displayName: "Synthetic Healthcheck Payload Contract",
        description: "Contract defining the fixed synthetic payload used for healthchecks. No user data, no user prompt, no dynamic content.",
        sourceModules: ["smart-shikka-assistant-provider-healthcheck"],
        targetPreviewModules: ["smart-shikka-assistant-provider-healthcheck"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Synthetic payload only", "No user data in payload", "No dynamic content"],
        futurePrScope: ["Payload validation enforcement"],
        notes: ["Fixed synthetic payload: Smart Shikka healthcheck ping", "No PII, no user content"],
    },
    {
        id: "redacted_healthcheck_result_contract",
        displayName: "Redacted Healthcheck Result Contract",
        description: "Contract defining how healthcheck results are redacted. All secrets, tokens, and provider responses are redacted in evidence.",
        sourceModules: ["smart-shikka-assistant-provider-healthcheck"],
        targetPreviewModules: ["smart-shikka-assistant-provider-healthcheck"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["All results must be redacted", "No raw provider response in logs", "No unredacted secrets"],
        futurePrScope: ["Live redaction enforcement"],
        notes: ["Results redacted as [REDACTED_HEALTHCHECK_RESULT]", "Provider response body never stored"],
    },
    {
        id: "provider_timeout_budget_policy",
        displayName: "Provider Timeout Budget Policy",
        description: "Policy metadata for healthcheck timeout and budget limits. No enforcement in PR94.",
        sourceModules: ["smart-shikka-assistant-provider-healthcheck"],
        targetPreviewModules: ["smart-shikka-assistant-provider-healthcheck"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Timeout policy is metadata only", "No enforcement in PR94"],
        futurePrScope: ["Timeout enforcement in future PR"],
        notes: ["Default timeout: 5000ms", "Max budget per healthcheck: configurable"],
    },
    {
        id: "provider_error_redaction_policy",
        displayName: "Provider Error Redaction Policy",
        description: "Policy for redacting provider error details. Stack traces, API keys, and internal paths must be redacted.",
        sourceModules: ["smart-shikka-assistant-provider-healthcheck"],
        targetPreviewModules: ["smart-shikka-assistant-provider-healthcheck"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Error details must be redacted", "No stack traces in evidence", "No API keys in logs"],
        futurePrScope: ["Live error redaction enforcement"],
        notes: ["Errors redacted as [REDACTED_PROVIDER_ERROR]", "Only status code preserved"],
    },
    {
        id: "no_user_prompt_boundary",
        displayName: "No User Prompt Boundary",
        description: "Hard boundary: healthcheck must never send user prompts, user text, or any user-originated content to providers.",
        sourceModules: ["smart-shikka-assistant-provider-healthcheck"],
        targetPreviewModules: ["smart-shikka-assistant-provider-healthcheck"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["No user prompt in healthcheck", "No user text to provider", "Synthetic payload only"],
        futurePrScope: ["User prompt gate unlock in future PR with separate approval"],
        notes: ["user_prompt_send blocked", "Only synthetic fixed payload allowed"],
    },
    {
        id: "no_chat_send_boundary",
        displayName: "No Chat Send Boundary",
        description: "Hard boundary: healthcheck must never send chat messages or trigger chat runtime.",
        sourceModules: ["smart-shikka-assistant-provider-healthcheck"],
        targetPreviewModules: ["smart-shikka-assistant-provider-healthcheck"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["No chat send in healthcheck", "No message_send", "No chat_runtime_start"],
        futurePrScope: ["Chat provider call gate in future PR"],
        notes: ["message_send blocked", "chat_runtime_start blocked"],
    },
    {
        id: "future_chat_provider_call_gate",
        displayName: "Future Chat Provider Call Gate",
        description: "Placeholder for future chat-driven provider call gate. Locked in PR94. Requires separate approval flow.",
        sourceModules: ["smart-shikka-assistant-provider-healthcheck"],
        targetPreviewModules: [],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["provider_call_blocked_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_provider_call_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_provider_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Chat provider call gate locked", "Future scope only", "Requires separate approval"],
        futurePrScope: ["Chat-driven provider call with user prompt approval", "Per-message approval gate"],
        notes: ["Gate locked in PR94", "No user prompt or chat send"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantProviderHealthcheckEntries() {
    return registry;
}
export function getSmartShikkaAssistantProviderHealthcheckEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantProviderHealthcheckRegistry.js.map